import React, { useState, useId } from 'react';
import { DashboardChartPoint } from '../../types';

interface AdminRevenueChartProps {
  data: DashboardChartPoint[];
  currency?: string;
}

export const AdminRevenueChart: React.FC<AdminRevenueChartProps> = ({ data, currency = 'Ks' }) => {
  const [activeSeries, setActiveSeries] = useState<'both' | 'revenue' | 'profit'>('both');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const chartId = useId();

  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-plum-muted">
        No transaction data recorded for this timeframe
      </div>
    );
  }

  // Chart dimensions
  const width = 500;
  const height = 200;
  const padding = { top: 20, right: 15, bottom: 30, left: 55 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Max value calculation
  const maxVal = Math.max(
    ...data.map((d) => Math.max(d.revenue, d.profit)),
    1000 // Minimum floor
  );
  // Rounded ceiling for nice grid steps
  const ceiling = Math.ceil(maxVal / 1000) * 1000;

  const getX = (index: number) => {
    if (data.length <= 1) return padding.left + chartWidth / 2;
    return padding.left + (index / (data.length - 1)) * chartWidth;
  };

  const getY = (val: number) => {
    const clamped = Math.max(0, val);
    return padding.top + chartHeight - (clamped / ceiling) * chartHeight;
  };

  // Build SVG Path strings
  const buildLinePath = (key: 'revenue' | 'profit') => {
    return data
      .map((d, i) => {
        const x = getX(i);
        const y = getY(d[key]);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  };

  const buildAreaPath = (key: 'revenue' | 'profit') => {
    const line = buildLinePath(key);
    const bottomY = (padding.top + chartHeight).toFixed(1);
    const firstX = getX(0).toFixed(1);
    const lastX = getX(data.length - 1).toFixed(1);
    return `${line} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
  };

  const revenueArea = buildAreaPath('revenue');
  const revenueLine = buildLinePath('revenue');
  const profitArea = buildAreaPath('profit');
  const profitLine = buildLinePath('profit');

  // Format Kyat numbers nicely
  const formatShortAmount = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const formatFullAmount = (num: number) => {
    return `${num.toLocaleString()} ${currency}`;
  };

  // Y-axis grid marks (0, 50%, 100%)
  const yTicks = [0, ceiling * 0.5, ceiling];

  // Selected or hovered point
  const activePoint = hoverIndex !== null && data[hoverIndex] ? data[hoverIndex] : data[data.length - 1];

  return (
    <div className="w-full select-none">
      {/* Series Filters & Live Indicator */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1 bg-surface-page/80 p-1 rounded-lg border border-plum-border text-xs">
          <button
            type="button"
            onClick={() => setActiveSeries('both')}
            className={`px-2.5 py-1 rounded-md transition font-medium ${
              activeSeries === 'both' ? 'bg-surface-card text-plum-dark shadow-xs' : 'text-plum-muted hover:text-plum-dark'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setActiveSeries('revenue')}
            className={`px-2.5 py-1 rounded-md transition font-medium flex items-center gap-1.5 ${
              activeSeries === 'revenue'
                ? 'bg-primary/20 text-primary-light border border-primary/40'
                : 'text-plum-muted hover:text-primary-light'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-primary inline-block" />
            Revenue
          </button>
          <button
            type="button"
            onClick={() => setActiveSeries('profit')}
            className={`px-2.5 py-1 rounded-md transition font-medium flex items-center gap-1.5 ${
              activeSeries === 'profit'
                ? 'bg-status-green/20 text-emerald-400 border border-status-green/40'
                : 'text-plum-muted hover:text-emerald-400'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-status-green inline-block" />
            Profit
          </button>
        </div>

        {/* Highlighted Value Card */}
        {activePoint && (
          <div className="text-right text-xs">
            <span className="text-plum-muted block">
              {activePoint.date} • {activePoint.ordersCount} orders
            </span>
            <div className="flex items-center justify-end gap-2 font-semibold">
              {(activeSeries === 'both' || activeSeries === 'revenue') && (
                <span className="text-primary-light">
                  Rev: {formatShortAmount(activePoint.revenue)} {currency}
                </span>
              )}
              {(activeSeries === 'both' || activeSeries === 'profit') && (
                <span className="text-emerald-400">
                  Prof: +{formatShortAmount(activePoint.profit)} {currency}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SVG Interactive Chart */}
      <div className="relative w-full overflow-hidden rounded-xl bg-surface-card border border-plum-border/70 p-2">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-44 sm:h-52 overflow-visible"
          onMouseLeave={() => setHoverIndex(null)}
          onTouchEnd={() => setHoverIndex(null)}
        >
          <defs>
            {/* Revenue Gradient */}
            <linearGradient id={`revGrad-${chartId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.0" />
            </linearGradient>

            {/* Profit Gradient */}
            <linearGradient id={`profGrad-${chartId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {yTicks.map((tick, i) => {
            const y = getY(tick);
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={width - padding.right}
                  y2={y}
                  stroke="#1E293B"
                  strokeDasharray="3 3"
                  strokeWidth="1"
                />
                <text
                  x={padding.left - 6}
                  y={y + 3}
                  textAnchor="end"
                  fill="#64748B"
                  fontSize="10"
                  fontFamily="sans-serif"
                >
                  {formatShortAmount(tick)}
                </text>
              </g>
            );
          })}

          {/* Revenue Area & Line */}
          {(activeSeries === 'both' || activeSeries === 'revenue') && (
            <g>
              <path d={revenueArea} fill={`url(#revGrad-${chartId})`} />
              <path
                d={revenueLine}
                fill="none"
                stroke="#0EA5E9"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          )}

          {/* Profit Area & Line */}
          {(activeSeries === 'both' || activeSeries === 'profit') && (
            <g>
              <path d={profitArea} fill={`url(#profGrad-${chartId})`} />
              <path
                d={profitLine}
                fill="none"
                stroke="#10B981"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          )}

          {/* Scrubber vertical guide & highlight dots */}
          {hoverIndex !== null && (
            <g>
              <line
                x1={getX(hoverIndex)}
                y1={padding.top}
                x2={getX(hoverIndex)}
                y2={padding.top + chartHeight}
                stroke="#94A3B8"
                strokeWidth="1.5"
                strokeDasharray="2 2"
              />
              {(activeSeries === 'both' || activeSeries === 'revenue') && (
                <circle
                  cx={getX(hoverIndex)}
                  cy={getY(data[hoverIndex].revenue)}
                  r="5"
                  fill="#0EA5E9"
                  stroke="#0A0E17"
                  strokeWidth="2"
                />
              )}
              {(activeSeries === 'both' || activeSeries === 'profit') && (
                <circle
                  cx={getX(hoverIndex)}
                  cy={getY(data[hoverIndex].profit)}
                  r="5"
                  fill="#10B981"
                  stroke="#0A0E17"
                  strokeWidth="2"
                />
              )}
            </g>
          )}

          {/* Touch / Clickable columns across dataset */}
          {data.map((_, i) => {
            const colWidth = chartWidth / data.length;
            const x = getX(i) - colWidth / 2;
            return (
              <rect
                key={i}
                x={Math.max(padding.left, x)}
                y={padding.top}
                width={colWidth}
                height={chartHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoverIndex(i)}
                onTouchStart={() => setHoverIndex(i)}
              />
            );
          })}

          {/* X-axis date labels: first, middle, last */}
          {data.length > 0 && (
            <g fill="#64748B" fontSize="10" fontFamily="sans-serif">
              <text x={padding.left} y={height - 8} textAnchor="start">
                {data[0].date.slice(5)}
              </text>
              {data.length > 2 && (
                <text
                  x={getX(Math.floor(data.length / 2))}
                  y={height - 8}
                  textAnchor="middle"
                >
                  {data[Math.floor(data.length / 2)].date.slice(5)}
                </text>
              )}
              <text x={width - padding.right} y={height - 8} textAnchor="end">
                {data[data.length - 1].date.slice(5)}
              </text>
            </g>
          )}
        </svg>

        {/* Hover / Tap tooltip details overlay */}
        {hoverIndex !== null && data[hoverIndex] && (
          <div className="mt-2 pt-2 border-t border-plum-border flex items-center justify-between text-xs text-plum-muted px-1">
            <span>Date: <strong className="text-plum-dark">{data[hoverIndex].date}</strong></span>
            <span>Completed Orders: <strong className="text-plum-dark">{data[hoverIndex].ordersCount}</strong></span>
            <span>Rev: <strong className="text-primary-light">{formatFullAmount(data[hoverIndex].revenue)}</strong></span>
            <span>Net Profit: <strong className="text-emerald-400">+{formatFullAmount(data[hoverIndex].profit)}</strong></span>
          </div>
        )}
      </div>
    </div>
  );
};
